import { useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  keyframes,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FacebookIcon from "@mui/icons-material/Facebook";
import SpaIcon from "@mui/icons-material/Spa";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import { LayoutContext } from "../../context/LayoutContext";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const SnapshotCard = ({
  icon: Icon,
  iconBgColor,
  iconColor,
  label,
  value,
  subtitle,
  subtitleColor,
  showTrend,
  avatarSrc,
  delay,
  collapsed,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: { xs: "45%", sm: collapsed ? 150 : 180 },
        p: collapsed ? 1.5 : 2,
        transition: "all 0.3s ease",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "grey.200",
        backgroundColor: "#fff",
        height:"100px",
        animation: `${fadeInUp} 0.5s ease-out ${delay}s both`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
          borderColor: iconColor,
        },
      }}
    >
      {/* Label */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: "#94A3B8",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1.5,
        }}
      >
        {label}
      </Typography>

      {/* Content */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 ,mt:3}}>
        {avatarSrc ? (
          <Avatar
            src={avatarSrc}
            sx={{
              width: 40,
              height: 40,
              animation: `${scaleIn} 0.4s ease-out ${delay + 0.2}s both`,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: iconBgColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: `${scaleIn} 0.4s ease-out ${delay + 0.2}s both`,
            }}
          >
            <Icon sx={{ fontSize: 20, color: iconColor }} />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 18,
              color: "#1E293B",
              lineHeight: 1.2,
              mb: 0.25,
            }}
            noWrap
          >
            {value}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {showTrend && (
              <TrendingUpIcon
                sx={{
                  fontSize: 12,
                  color: subtitleColor || "#22C55E",
                }}
              />
            )}
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: subtitleColor || "text.secondary",
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// Compute performance snapshot stats from leads
const computeSnapshotStats = (leads) => {
  if (!leads || leads.length === 0) {
    return [
      {
        label: "Top Source",
        icon: FacebookIcon,
        iconBgColor: alpha("#1877F2", 0.1),
        iconColor: "#1877F2",
        value: "N/A",
        subtitle: "No data available",
        subtitleColor: "text.secondary",
        showTrend: false,
      },
      {
        label: "Top Service",
        icon: SpaIcon,
        iconBgColor: alpha("#8B5CF6", 0.1),
        iconColor: "#8B5CF6",
        value: "N/A",
        subtitle: "No data available",
        subtitleColor: "text.secondary",
        showTrend: false,
      },
      {
        label: "Best Employee",
        icon: PersonIcon,
        iconBgColor: alpha("#F59E0B", 0.1),
        iconColor: "#F59E0B",
        value: "N/A",
        subtitle: "No data available",
        subtitleColor: "text.secondary",
        showTrend: false,
      },
      {
        label: "Avg Conversion Time",
        icon: AccessTimeIcon,
        iconBgColor: alpha("#10B981", 0.1),
        iconColor: "#10B981",
        value: "N/A",
        subtitle: "No data available",
        subtitleColor: "text.secondary",
        showTrend: false,
      },
    ];
  }

  // 1. Top Source - most common lead_source in custom_fields
  const sourceMap = {};
  leads.forEach((lead) => {
    const source = lead.custom_fields?.lead_source || "Unknown";
    sourceMap[source] = (sourceMap[source] || 0) + 1;
  });
  const topSource = Object.entries(sourceMap).sort((a, b) => b[1] - a[1])[0];
  const topSourceCount = topSource ? topSource[1] : 0;
  const topSourcePercent = leads.length > 0 ? ((topSourceCount / leads.length) * 100).toFixed(0) : 0;

  // 2. Top Service - most common service in services array
  const serviceMap = {};
  leads.forEach((lead) => {
    if (Array.isArray(lead.services)) {
      lead.services.forEach((service) => {
        const serviceName = typeof service === "string" ? service : service?.name || "Unknown";
        serviceMap[serviceName] = (serviceMap[serviceName] || 0) + 1;
      });
    }
  });
  const topService = Object.entries(serviceMap).sort((a, b) => b[1] - a[1])[0];
  const topServiceCount = topService ? topService[1] : 0;
  const totalServicesWithLeads = Object.values(serviceMap).reduce((sum, count) => sum + count, 0);
  const topServicePercent = totalServicesWithLeads > 0 ? ((topServiceCount / totalServicesWithLeads) * 100).toFixed(0) : 0;

  // 3. Best Employee - employee with most converted leads (exclude Unassigned)
  const employeeMap = {};
  leads.forEach((lead) => {
    const employee = lead.assign_employee?.name || lead.assignee;
    // Skip if no employee assigned or Unassigned
    if (!employee || employee === "Unassigned") return;

    if (!employeeMap[employee]) {
      employeeMap[employee] = { count: 0, converted: 0, totalLeads: 0 };
    }
    employeeMap[employee].totalLeads++;

    const status = lead.current_status?.key?.toLowerCase() || "";
    if (["converted", "done", "closed", "won"].includes(status)) {
      employeeMap[employee].converted++;
      employeeMap[employee].count++;
    }
  });

  // Sort by most conversions first, then by total leads
  const bestEmployee = Object.entries(employeeMap)
    .sort((a, b) => b[1].converted - a[1].converted || b[1].totalLeads - a[1].totalLeads)[0];

  const bestEmployeeName = bestEmployee ? bestEmployee[0] : null;
  const bestEmployeeCount = bestEmployee ? bestEmployee[1].converted : 0;
  const bestEmployeeTotalLeads = bestEmployee ? bestEmployee[1].totalLeads : 0;

  // Calculate satisfaction rate (converted leads / total leads assigned)
  const satisfactionRate =
    bestEmployeeTotalLeads > 0
      ? ((bestEmployeeCount / bestEmployeeTotalLeads) * 100).toFixed(0)
      : "0";

  // 4. Avg Conversion Time - avg days from createdAt to converted status
  const conversionTimes = [];
  leads.forEach((lead) => {
    const status = lead.current_status?.key?.toLowerCase() || "";
    if (["converted", "done", "closed", "won"].includes(status)) {
      const created = new Date(lead.createdAt);
      const updated = new Date(lead.updatedAt);
      const daysDiff = Math.floor((updated - created) / (1000 * 60 * 60 * 24));
      if (daysDiff >= 0) {
        conversionTimes.push(daysDiff);
      }
    }
  });

  const avgConversionDays =
    conversionTimes.length > 0
      ? (conversionTimes.reduce((sum, days) => sum + days, 0) / conversionTimes.length).toFixed(1)
      : "0";

  return [
    {
      label: "Top Source",
      icon: FacebookIcon,
      iconBgColor: alpha("#1877F2", 0.1),
      iconColor: "#1877F2",
      value: topSource ? topSource[0] : "N/A",
      subtitle: topSource ? `${topSourcePercent}% of total leads` : "No data",
      subtitleColor: "#22C55E",
      showTrend: true,
    },
    {
      label: "Top Service",
      icon: SpaIcon,
      iconBgColor: alpha("#8B5CF6", 0.1),
      iconColor: "#8B5CF6",
      value: topService ? topService[0] : "N/A",
      subtitle: topService ? `${topServicePercent}% of services` : "No data",
      subtitleColor: "text.secondary",
      showTrend: false,
    },
    {
      label: "Best Employee",
      icon: PersonIcon,
      iconBgColor: alpha("#F59E0B", 0.1),
      iconColor: "#F59E0B",
      value: bestEmployeeName || "No employees assigned",
      subtitle: bestEmployee ? `${bestEmployeeCount} conversions (${satisfactionRate}% rate)` : "Assign leads to employees",
      subtitleColor: "text.secondary",
      showTrend: false,
    },
    {
      label: "Avg Conversion Time",
      icon: AccessTimeIcon,
      iconBgColor: alpha("#10B981", 0.1),
      iconColor: "#10B981",
      value: `${avgConversionDays} Days`,
      subtitle: conversionTimes.length > 0 ? `${conversionTimes.length} leads converted` : "No data",
      subtitleColor: "#22C55E",
      showTrend: true,
    },
  ];
};

const PerformanceSnapshot = ({ stats, leads = [] }) => {
  const { collapsed } = useContext(LayoutContext);

  // Compute realtime snapshot data from leads
  const snapshotData = useMemo(() => computeSnapshotStats(leads), [leads]);

  return (
    <Box  px={3}>
      {/* Section Header */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 14,
          color: "#64748B",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 2,
        }}
      >
        Performance Snapshot
      </Typography>

      {/* Snapshot Cards */}
      <Box
        sx={{
          display: "flex",
          gap: collapsed ? 1.5 : 2,
          flexWrap: { xs: "wrap", md: collapsed ? "nowrap" : "wrap", lg: "nowrap" },
          transition: "all 0.3s ease",
        }}
      >
        {snapshotData.map((item, index) => (
          <SnapshotCard
            key={item.label}
            label={item.label}
            icon={item.icon}
            iconBgColor={item.iconBgColor}
            iconColor={item.iconColor}
            value={item.value}
            subtitle={item.subtitle}
            subtitleColor={item.subtitleColor}
            showTrend={item.showTrend}
            avatarSrc={item.avatarSrc}
            delay={index * 0.1}
            collapsed={collapsed}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PerformanceSnapshot;
